import type { LeadFormDeliveryContext, LeadFormType } from "./types";

import { careSeekerContractSchema } from "../schemas/care-seeker-contract";
import {
  careRecipientSchema,
  urgencySchema,
} from "../schemas/care-seeker-form";
import {
  HONEYPOT_FIELD_NAME,
  LOCATION_FIELD_NAME,
  TIMING_TOKEN_FIELD_NAME,
} from "./lead-form-field-names";

const LEAD_FORM_TYPE = {
  careSeeker: "careSeeker",
} as const;

const LEAD_FORM_DELIVERY_CONTEXT = {
  national: "national",
  officeLocal: "officeLocal",
} as const;

const CARE_FOR_VALUES = careRecipientSchema.options;

const URGENCY_VALUES = urgencySchema.options;

const CARE_TYPE_VALUES = [
  "everydayTasks",
  "memoryCare",
  "mobilityAssistance",
  "other",
] as const;

const RESERVED_LEAD_FORM_FIELD_NAMES = new Set([
  "careFor",
  "careType",
  "consentTerms",
  "email",
  HONEYPOT_FIELD_NAME,
  "firstName",
  TIMING_TOKEN_FIELD_NAME,
  "formVariant",
  "lastName",
  "leadSource",
  "location",
  "OptInSms",
  "organizationNumber",
  "phone",
  "postalCode",
  "urgency",
]);

const CONTRACT_OPTION_FIELD_NAMES = new Set(["careFor", "careType", "urgency"]);

const LEAD_FORM_CONTRACTS = {
  careSeeker: {
    fieldOptionValues: {
      careFor: CARE_FOR_VALUES,
      careType: CARE_TYPE_VALUES,
      urgency: URGENCY_VALUES,
    },
    // The office a visitor picks after entering a postal code: the typed
    // postal input and the chosen office number. Non-personal, like the
    // postal code it resolves from.
    officeSelectionFieldNames: [
      LOCATION_FIELD_NAME,
      "organizationNumber",
    ] as const,
    postalSource: {
      national: "authoredVisibleInput",
      officeLocal: "runtimeInjected",
    },
    // Non-personal fields only. Contact fields and SMS consent must never be
    // read from the URL, which reaches history, logs, analytics, and referrers.
    queryPrefillParameters: {
      careFor: "careFor",
      careType: "careType",
      postalCode: "postalCode",
      urgency: "urgency",
    },
    requiredFieldNames: ["email", "firstName", "lastName", "phone"] as const,
    submitSchema: careSeekerContractSchema,
    systemProvidedFieldNames: [
      "formVariant",
      "leadSource",
      "organizationNumber",
    ],
  },
} as const;

function isLeadFormType(
  value: string | null | undefined,
): value is LeadFormType {
  return value != null && value in LEAD_FORM_CONTRACTS;
}

function isLeadFormDeliveryContext(
  value: string | null | undefined,
): value is LeadFormDeliveryContext {
  return (
    value === LEAD_FORM_DELIVERY_CONTEXT.national ||
    value === LEAD_FORM_DELIVERY_CONTEXT.officeLocal
  );
}

function isReservedLeadFormFieldName(fieldName: string): boolean {
  return RESERVED_LEAD_FORM_FIELD_NAMES.has(fieldName);
}

function isContractOptionFieldName(fieldName: string): boolean {
  return CONTRACT_OPTION_FIELD_NAMES.has(fieldName);
}

export {
  CARE_FOR_VALUES,
  CARE_TYPE_VALUES,
  CONTRACT_OPTION_FIELD_NAMES,
  isContractOptionFieldName,
  isLeadFormDeliveryContext,
  isLeadFormType,
  isReservedLeadFormFieldName,
  LEAD_FORM_CONTRACTS,
  LEAD_FORM_DELIVERY_CONTEXT,
  LEAD_FORM_TYPE,
  RESERVED_LEAD_FORM_FIELD_NAMES,
  URGENCY_VALUES,
};
